import i18n from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Document, DocumentLook } from "@/lib/api";
import { useThemes } from "@/hooks/useThemes";
import { documentChapters, effectiveTheme, normaliseLook } from "@/lib/themes/look";
import { testIds } from "@/testIds";
import { themeLabel } from "../stampText";
import { useEditorStore } from "@/store/editorStore";
import { LookSection } from "@/components/editor/look/LookSection";
import styles from "../DocumentStamp.module.scss";

interface LookStampSectionProps {
  document: Document;
  disabled?: boolean;
  /** Saves the look; rejects when the server refuses it. */
  onSave: (look: DocumentLook) => Promise<void>;
  /**
   * Opened from the stamp's theme name: scroll Look into view and focus its
   * header (REPLY-2026-10-06d §1), so Look is reached without moving it up.
   */
  focusOnOpen?: boolean;
}

/**
 * Look in the document popover (design-handoffs/2026-10-05-document-themes,
 * 1e). The popover is the document settings people reach from the editor, so
 * the theme lives here and saves at once, like the class options beside it.
 * A refused look goes back to the last good one.
 */
export function LookStampSection({ document, disabled = false, onSave, focusOnOpen = false }: LookStampSectionProps) {
  const savedLook = document.look ?? null;
  const savedKey = JSON.stringify(savedLook);
  const [look, setLook] = useState<DocumentLook>(() => normaliseLook(savedLook));
  useEffect(() => {
    setLook(normaliseLook(savedLook));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.id, savedKey]);

  const { themes, isLoading, error } = useThemes();
  const blocks = useEditorStore((s) => s.blocks);
  const chapters = useMemo(() => documentChapters(blocks), [blocks]);

  // "Look · Carnet": the theme the document prints with, named in the header.
  const shownId = effectiveTheme(look, document.lookLocked, document.lookThemes);
  const shownName = themeLabel(shownId, themes);

  const sectionRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!focusOnOpen) return;
    headerRef.current?.focus({ preventScroll: true });
    // After the popover has been placed, so only its own scroller moves.
    const raf = window.requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView?.({ block: "start", inline: "nearest" });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [focusOnOpen]);

  async function change(next: DocumentLook) {
    if (disabled) return;
    const prev = look;
    setLook(next);
    try {
      await onSave(next);
    } catch {
      setLook(prev);
    }
  }

  return (
    <div ref={sectionRef} className={styles.section} data-testid="document-stamp-look">
      {/* Stays put while the thumbnails scroll; the stamp's theme name lands here. */}
      <div
        ref={headerRef}
        tabIndex={-1}
        className={`${styles.sectionHeader} ${styles.lookHeader}`}
        data-testid={testIds.look.header}
        data-theme={shownId}
      >
        <span className={styles.sectionTitle}>
          {i18n.t("editor:stamp.look")} <span aria-hidden="true">·</span> {shownName}
        </span>
        {disabled && <span className={styles.readOnlyPip}>{i18n.t("editor:stamp.readOnly")}</span>}
      </div>
      <div className={disabled ? styles.muted : undefined}>
        <LookSection
          value={look}
          onChange={change}
          themes={themes}
          themesLoading={isLoading}
          themesError={!!error}
          lookLocked={document.lookLocked ?? null}
          lookThemes={document.lookThemes ?? null}
          chapters={chapters}
        />
      </div>
    </div>
  );
}
